import { EnterOutlined } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  Button,
  Space,
  TableColumnsType,
  TablePaginationConfig,
  Tag,
  theme,
} from 'antd'

import MembershipProfile from '@components/membership/membership-profile/MembershipProfile'
import Page from '@components/Page/Page'
import EventParticipation from '@components/participation/Participation'
import { EventParticipation as EventParticipationModel } from '@models/Event'
import { checkIn } from '@services/EventParticipationService'
import {
  getEventParticipations,
  selectEventParticipations,
  setEventParticipationQuery,
} from '@store/membership/EventParticipationReducer'
import { selectSelectedEvent } from '@store/membership/EventReducer'
import { extractSortDataFromSorterResult } from '@supplement-bacon/react-toolkit/utils'
import {
  initialQuery,
  SBTable,
  TableParams,
} from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { SorterResult } from 'antd/es/table/interface'
import { useEffect } from 'react'

const ParticipantsTab = () => {
  const { token } = theme.useToken()
  const dispatch = useAppDispatch()
  const event = useAppSelector(selectSelectedEvent())
  const participations = useAppSelector(selectEventParticipations())
  const { query, isLoading } = useAppSelector(
    (state) => state.eventParticipation
  )

  const defaultQuery: TableParams = {
    columnKey: 'status',
    order: 'ascend',
    with: ['membershipProfile'],
    filters: {
      events: [event?.id ?? 0],
      withTrashed: [true],
    },
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    sorter:
      | SorterResult<EventParticipationModel>
      | SorterResult<EventParticipationModel>[]
  ) => {
    const newQuery: TableParams = {
      ...query,
      ...extractSortDataFromSorterResult(sorter),
      pagination,
      ...defaultQuery,
    }
    dispatch(setEventParticipationQuery(newQuery))
    dispatch(getEventParticipations(newQuery))
  }

  const fetchParticipations = () => {
    const query: TableParams = {
      ...initialQuery,
      ...defaultQuery,
    }
    dispatch(setEventParticipationQuery(query))
    dispatch(getEventParticipations(query))
  }

  useEffect(() => {
    if (!event) return
    fetchParticipations()
  }, [event])

  const isGuest = (participation: EventParticipationModel): boolean => {
    return participation.firstName != undefined && participation.firstName != ''
  }
  const columns: TableColumnsType<EventParticipationModel> = [
    {
      title: 'Participant',
      render: (record: EventParticipationModel) =>
        isGuest(record) ? (
          <EventParticipation.GuestAvatar model={record} />
        ) : (
          <MembershipProfile.Avatar id={record.membershipProfile} />
        ),
    },
    {
      title: 'Booked by',
      render: (record: EventParticipationModel) =>
        isGuest(record) && (
          <MembershipProfile.Avatar id={record.membershipProfile} />
        ),
    },
    {
      title: 'Status',
      render: (record: EventParticipationModel) => (
        <>
          {record.deletedAt ? (
            <Tag color="red">Cancelled</Tag>
          ) : (
            <EventParticipation.StatusManager
              model={record}
              onChange={fetchParticipations}
            />
          )}
        </>
      ),
    },
    {
      align: 'right',
      render: (record: EventParticipationModel) => (
        <Space>
          {!record.checkedInAt && !record.deletedAt && (
            <Button
              type="primary"
              icon={<EnterOutlined />}
              onClick={() => {
                checkIn(record.id).finally(() => {
                  window.location.reload()
                })
              }}
            >
              Check-in
            </Button>
          )}
          <SBRM.Actions
            actions={
              record.deletedAt
                ? [SBRMAction.View]
                : [SBRMAction.View, SBRMAction.Delete]
            }
            entity={Entity.eventParticipation}
            entityId={record.id}
          />
        </Space>
      ),
    },
  ]
  const rowDisabledStyles: React.CSSProperties = {
    opacity: 0.5,
    backgroundColor: token.colorFillSecondary,
  }

  return (
    <Page>
      <SBTable
        loading={isLoading}
        columns={columns}
        pagination={query.pagination}
        dataSource={participations}
        onChange={handleTableChange}
        onRow={(record) => ({
          style: record.deletedAt ? rowDisabledStyles : {},
        })}
      />
    </Page>
  )
}

export default ParticipantsTab
